import axios from 'axios';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3001/api',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for authentication
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized access
      localStorage.removeItem('authToken');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// API endpoints
export const apiEndpoints = {
  // Authentication
  login: '/auth/login',
  register: '/auth/register',
  logout: '/auth/logout',
  refreshToken: '/auth/refresh',
  
  // Search
  searchDoctors: '/doctors/search',
  searchBySymptoms: '/doctors/search/symptoms',
  searchBySpecialty: '/doctors/search/specialty',
  searchByLocation: '/doctors/search/location',
  
  // Doctors
  getDoctors: '/doctors',
  getDoctor: '/doctors/:id',
  getDoctorAvailability: '/doctors/:id/availability',
  getDoctorReviews: '/doctors/:id/reviews',
  
  // Specialties
  getSpecialties: '/specialties',
  getSpecialty: '/specialties/:id',
  
  // Appointments
  getAppointments: '/appointments',
  createAppointment: '/appointments',
  updateAppointment: '/appointments/:id',
  cancelAppointment: '/appointments/:id/cancel',
  
  // Patient
  getPatientProfile: '/patients/profile',
  updatePatientProfile: '/patients/profile',
  getPatientAppointments: '/patients/appointments',
  
  // Emergency
  emergencyContact: '/emergency/contact',
  
  // Telemedicine
  getTelemedicineSlots: '/telemedicine/slots',
  createTelemedicineAppointment: '/telemedicine/appointments',
};

// Search API functions
export const searchAPI = {
  // Search doctors by query
  searchDoctors: async (query, filters = {}) => {
    const response = await api.get(apiEndpoints.searchDoctors, {
      params: { query, ...filters }
    });
    return response.data;
  },
  
  // Search by symptoms
  searchBySymptoms: async (symptoms, location = null) => {
    const response = await api.post(apiEndpoints.searchBySymptoms, {
      symptoms,
      location
    });
    return response.data;
  },
  
  // Search by specialty
  searchBySpecialty: async (specialty, location = null) => {
    const response = await api.get(apiEndpoints.searchBySpecialty, {
      params: { specialty, location }
    });
    return response.data;
  },
  
  // Search by location
  searchByLocation: async (location, radius = 10) => {
    const response = await api.get(apiEndpoints.searchByLocation, {
      params: { location, radius }
    });
    return response.data;
  },
};

// Doctors API functions
export const doctorsAPI = {
  // Get all doctors with pagination
  getDoctors: async (page = 1, limit = 20, filters = {}) => {
    const response = await api.get(apiEndpoints.getDoctors, {
      params: { page, limit, ...filters }
    });
    return response.data;
  },
  
  // Get single doctor
  getDoctor: async (id) => {
    const response = await api.get(apiEndpoints.getDoctor.replace(':id', id));
    return response.data;
  },
  
  // Get doctor availability
  getDoctorAvailability: async (id, date) => {
    const response = await api.get(apiEndpoints.getDoctorAvailability.replace(':id', id), {
      params: { date }
    });
    return response.data;
  },
  
  // Get doctor reviews
  getDoctorReviews: async (id, page = 1, limit = 10) => {
    const response = await api.get(apiEndpoints.getDoctorReviews.replace(':id', id), {
      params: { page, limit }
    });
    return response.data;
  },
};

// Specialties API functions
export const specialtiesAPI = {
  // Get all specialties
  getSpecialties: async () => {
    const response = await api.get(apiEndpoints.getSpecialties);
    return response.data;
  },
  
  // Get single specialty
  getSpecialty: async (id) => {
    const response = await api.get(apiEndpoints.getSpecialty.replace(':id', id));
    return response.data;
  },
};

// Appointments API functions
export const appointmentsAPI = {
  // Get user appointments
  getAppointments: async (filters = {}) => {
    const response = await api.get(apiEndpoints.getAppointments, {
      params: filters
    });
    return response.data;
  },
  
  // Create appointment
  createAppointment: async (appointmentData) => {
    const response = await api.post(apiEndpoints.createAppointment, appointmentData);
    return response.data;
  },
  
  // Update appointment
  updateAppointment: async (id, updateData) => {
    const response = await api.put(apiEndpoints.updateAppointment.replace(':id', id), updateData);
    return response.data;
  },
  
  // Cancel appointment
  cancelAppointment: async (id, reason) => {
    const response = await api.delete(apiEndpoints.cancelAppointment.replace(':id', id), {
      data: { reason }
    });
    return response.data;
  },
};

// Authentication API functions
export const authAPI = {
  // Login
  login: async (credentials) => {
    const response = await api.post(apiEndpoints.login, credentials);
    return response.data;
  },
  
  // Register
  register: async (userData) => {
    const response = await api.post(apiEndpoints.register, userData);
    return response.data;
  },
  
  // Logout
  logout: async () => {
    const response = await api.post(apiEndpoints.logout);
    return response.data;
  },
  
  // Refresh token
  refreshToken: async () => {
    const response = await api.post(apiEndpoints.refreshToken);
    return response.data;
  },
};

// Emergency API functions
export const emergencyAPI = {
  // Contact emergency services
  contactEmergency: async (emergencyData) => {
    const response = await api.post(apiEndpoints.emergencyContact, emergencyData);
    return response.data;
  },
};

// Telemedicine API functions
export const telemedicineAPI = {
  // Get telemedicine slots
  getTelemedicineSlots: async (doctorId, date) => {
    const response = await api.get(apiEndpoints.getTelemedicineSlots, {
      params: { doctorId, date }
    });
    return response.data;
  },
  
  // Create telemedicine appointment
  createTelemedicineAppointment: async (appointmentData) => {
    const response = await api.post(apiEndpoints.createTelemedicineAppointment, appointmentData);
    return response.data;
  },
};

export default api;
